import { Close, InfoOutlined } from "@mui/icons-material";
import {
  Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputAdornment,
  InputLabel, MenuItem, Select, Tab, Tabs, TextField, Tooltip, Typography, useTheme,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { getCurrentTime } from "../utils/assistantUtils";

const BRAND = "#005cb9";

const SCOPE_LABEL = "Nonprovisional Utility";
const SCOPE_BASIS = "35 U.S.C. 111(a)";
const SCOPE_TOOLTIP = "This calculator covers nonprovisional utility applications filed electronically under 111(a) only.";

const ENTITY_STATUS = [
  { name: "Large", value: "large" },
  { name: "Small", value: "small" },
  { name: "Micro", value: "micro" },
];

const APPLICATION_LANGUAGE = [
  { name: "English", value: "English" },
  { name: "Non-English", value: "Non-English" },
];

const TABS = ["Essentials", "Document & claims", "Special circumstances", "Record details"];

const NUMBER_FIELDS = [
  "totalClaims", "independentClaims", "specificationPages", "claimsPages",
  "abstractPages", "drawingPages", "sequenceListingSizeMB",
  "numberOfInventors",
];

const DEPENDENT_FIELDS = {
  earliestBenefitDate: "claimsEarlierApplication",
  sequenceListingSizeMB: "sequenceListingSubmitted",
};

const REQUIRED_PAGE_FIELDS = { specificationPages: "Specification", claimsPages: "Claims", abstractPages: "Abstract" };
const COMPULSORY_COUNTS = ["totalClaims", "independentClaims", "specificationPages", "claimsPages", "abstractPages"];
const TRACK_ONE_MAX_INDEPENDENT = 4;
const TRACK_ONE_MAX_TOTAL = 30;
const TRACK_ONE_INPUTS = ["totalClaims", "independentClaims", "multipleDependentClaimPresent"];

const INITIAL_FORM = {
  filingDate: "",
  applicationType: "NONPROVISIONAL_UTILITY",
  filingBasis: "111A",
  entityStatus: "",
  applicationTitle: "",
  numberOfInventors: "",
  totalClaims: "",
  independentClaims: "",
  multipleDependentClaimPresent: false,
  specificationPages: "",
  claimsPages: "",
  abstractPages: "",
  drawingPages: "",
  descriptionDocxCompliant: true,
  claimsDocxCompliant: true,
  abstractDocxCompliant: true,
  applicationLanguage: "English",
  claimsEarlierApplication: false,
  earliestBenefitDate: "",
  sequenceListingSubmitted: false,
  sequenceListingSizeMB: "",
  trackOneRequested: false,
  filingFeePaidLate: false,
  searchFeePaidLate: false,
  examFeePaidLate: false,
  oathFiledLate: false,
  filedWithoutClaims: false,
  filedByReference: false,
};

const DATE_MIN = "1900-01-01";
const DATE_MAX = "2100-12-31";
const isDateInRange = (v) => {
  const [yearStr] = String(v).split("-");
  const year = Number(yearStr);
  return yearStr?.length === 4 && Number.isInteger(year) && year >= 1900 && year <= 2100;
};

const FIELD_TAB = {
  filingDate: 0,
  entityStatus: 0,
  totalClaims: 0,
  independentClaims: 0,
  specificationPages: 0,
  claimsPages: 0,
  abstractPages: 0,
  drawingPages: 0,
  earliestBenefitDate: 2,
  sequenceListingSizeMB: 2,
  trackOneRequested: 2,
  numberOfInventors: 3,
};

export const buildPayload = (form) => {
  const payload = {};

  for (const [key, value] of Object.entries(form)) {
    const parent = DEPENDENT_FIELDS[key];
    if (parent && !form[parent]) continue;

    if (value === "" || value === null || value === undefined) continue;

    if (NUMBER_FIELDS.includes(key)) {
      const asNumber = Number(value);
      if (!Number.isFinite(asNumber)) continue;
      payload[key] = asNumber;
    } else {
      payload[key] = value;
    }
  }

  return payload;
};

export const validateBeforeSubmit = (form) => {
  const errors = {};
  if (!form.filingDate) errors.filingDate = "Filing date is required.";
  else if (!isDateInRange(form.filingDate)) errors.filingDate = "Enter a valid date.";
  if (!form.entityStatus) errors.entityStatus = "Entity status is required.";
  if (form.totalClaims === "") errors.totalClaims = "Total claim count is required.";
  if (form.independentClaims === "") errors.independentClaims = "Independent claim count is required.";

  for (const [field, label] of Object.entries(REQUIRED_PAGE_FIELDS)) {
    if (form[field] === "") errors[field] = `${label} page count is required.`;
  }
  if (form.claimsEarlierApplication && !form.earliestBenefitDate) {
    errors.earliestBenefitDate = "Required when benefit of an earlier application is claimed.";
  } else if (form.claimsEarlierApplication && !isDateInRange(form.earliestBenefitDate)) {
    errors.earliestBenefitDate = "Enter a valid date.";
  } else if (form.claimsEarlierApplication && !errors.filingDate && form.earliestBenefitDate > form.filingDate) {
    errors.earliestBenefitDate = "Cannot be later than the filing date.";
  }
  if (form.sequenceListingSubmitted && form.sequenceListingSizeMB === "") {
    errors.sequenceListingSizeMB = "Required when a sequence listing is submitted.";
  }

  // Buttons, not a native form - the browser's own min=0 / step=1 are never enforced.
  for (const field of NUMBER_FIELDS) {
    const parent = DEPENDENT_FIELDS[field];
    if (parent && !form[parent]) continue;
    if (errors[field] || form[field] === "") continue;
    const n = Number(form[field]);
    if (n < 0) errors[field] = "Must be zero or greater.";
    else if (field !== "sequenceListingSizeMB" && !Number.isInteger(n)) errors[field] = "Must be a whole number.";
    else if (n === 0 && COMPULSORY_COUNTS.includes(field)) errors[field] = "Must be at least 1.";
    else if (n === 0 && field === "sequenceListingSizeMB") errors[field] = "Must be greater than 0.";
  }

  if (!errors.totalClaims && !errors.independentClaims && Number(form.independentClaims) > Number(form.totalClaims)) {
    errors.independentClaims = "Cannot exceed total claims.";
  }

  if (form.trackOneRequested) {
    const problems = [];
    if (!errors.independentClaims && Number(form.independentClaims) > TRACK_ONE_MAX_INDEPENDENT) {
      problems.push(`${Number(form.independentClaims)} independent claims (max ${TRACK_ONE_MAX_INDEPENDENT})`);
    }
    if (!errors.totalClaims && Number(form.totalClaims) > TRACK_ONE_MAX_TOTAL) {
      problems.push(`${Number(form.totalClaims)} total claims (max ${TRACK_ONE_MAX_TOTAL})`);
    }
    if (form.multipleDependentClaimPresent) problems.push("multiple dependent claims are not permitted");
    if (problems.length) errors.trackOneRequested = `Track One not available: ${problems.join("; ")}.`;
  }
  return errors;
};

export const validateTab = (tabIndex, form) => {
  const allErrors = validateBeforeSubmit(form);
  return Object.fromEntries(
    Object.entries(allErrors).filter(([field]) => FIELD_TAB[field] === tabIndex)
  );
};

// The mask writes the "/" but leaves the caret behind it, so step over it.
const skipDateSeparator = (e) => {
  const el = e.target;
  const pos = el.selectionStart;
  if (/^\d$/.test(e.key) && el.value[pos] === "/") {
    el.setSelectionRange(pos + 1, pos + 1);
  }
};

const isFormTouched = (form) =>
  Object.keys(INITIAL_FORM).some((key) => form[key] !== INITIAL_FORM[key]);

const UsptoFeeCalculatorModal = ({
  setFeeCalcIntent,
  handleStop,
  setMessages,
  handleModuleSubmit,
  originatingRequestId,
  requestId,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [maxUnlockedTab, setMaxUnlockedTab] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const shortcutsRef = useRef();

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    const finalValue = type === "checkbox" ? checked : value;
    const dependents = Object.keys(DEPENDENT_FIELDS).filter((d) => DEPENDENT_FIELDS[d] === name);
    const clearDependents = !finalValue && dependents.length;

    setForm((prev) => {
      const next = { ...prev, [name]: finalValue };
      if (clearDependents) dependents.forEach((d) => { next[d] = INITIAL_FORM[d]; });
      return next;
    });
    setErrors((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      delete next[name];
      if (name === "totalClaims") delete next.independentClaims;
      if (name === "filingDate") delete next.earliestBenefitDate;
      if (TRACK_ONE_INPUTS.includes(name)) delete next.trackOneRequested;
      if (clearDependents) dependents.forEach((d) => delete next[d]);
      return next;
    });
  };

  const closeModal = () => {
    setFeeCalcIntent(false);
    handleStop();
    setMessages((prev) => [...prev, { from: "assistant", content: "Request cancelled.", time: getCurrentTime() }]);
  };

  const requestClose = () => {
    if (isFormTouched(form)) {
      setConfirmDiscard(true);
    } else {
      closeModal();
    }
  };

  const handleCalculate = async () => {
    const preflight = validateBeforeSubmit(form);
    if (Object.keys(preflight).length) {
      setErrors(preflight);
      const tabs = Object.keys(preflight).map((f) => FIELD_TAB[f]).filter((t) => t !== undefined);
      if (tabs.length) setActiveTab(Math.min(...tabs));
      return;
    }

    setFeeCalcIntent(false);
    await handleModuleSubmit({
      textInput: "USPTO Fee Details",
      largeSlotData: JSON.stringify(buildPayload(form)),
      originatingRequestId,
      requestId,
    });
  };

  const handleNext = () => {
    const tabErrors = validateTab(activeTab, form);
    if (Object.keys(tabErrors).length) {
      setErrors((prev) => ({ ...prev, ...tabErrors }));
      return;
    }
    setMaxUnlockedTab((prev) => Math.max(prev, activeTab + 1));
    setActiveTab((prev) => prev + 1);
  };

  // Ref-backed, not a dep array - avoids re-attaching the listener on every keystroke.
  shortcutsRef.current = { requestClose, handleNext, handleCalculate, activeTab, confirmDiscard };

  useEffect(() => {
    const onKeyDown = (e) => {
      const s = shortcutsRef.current;
      if (e.key === "Escape") {
        e.preventDefault();
        s.requestClose();
      } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !s.confirmDiscard) {
        e.preventDefault();
        if (s.activeTab < TABS.length - 1) s.handleNext();
        else s.handleCalculate();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const fieldSx = {
    "& label.Mui-focused": { color: BRAND },
    "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: BRAND } },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitBoxShadow: "0 0 0 100px #fefefe inset",
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
    },
  };

  const infoIcon = (tooltip) => (
    <InputAdornment position="end">
      <Tooltip title={tooltip} placement="top-end" arrow>
        <InfoOutlined sx={{ fontSize: "1rem", color: theme.palette.text.secondary }} />
      </Tooltip>
    </InputAdornment>
  );

  const textField = (name, label, extra = {}) => {
    const { helperText, ...rest } = extra;
    return (
      <TextField
        fullWidth
        label={label}
        name={name}
        value={form[name]}
        onChange={handleInputChange}
        error={!!errors[name]}
        InputLabelProps={{ shrink: true }}
        sx={fieldSx}
        {...rest}
        helperText={errors[name] || helperText || " "}
      />
    );
  };

  const numberField = (name, label, opts = {}) => {
    const { tooltip, helperText, required = false } = opts;
    const allowDecimal = name === "sequenceListingSizeMB";
    const clean = (raw) => {
      const stripped = raw.replace(allowDecimal ? /[^\d.]/g : /\D/g, "");
      return allowDecimal ? stripped.replace(/(\..*)\./g, "$1") : stripped;
    };
    return (
      <TextField
        fullWidth
        required={required}
        type="text"
        inputMode="numeric"
        label={label}
        name={name}
        value={form[name]}
        placeholder="0"
        onChange={(e) => handleInputChange({ target: { name, value: clean(e.target.value), type: "text" } })}
        error={!!errors[name]}
        helperText={errors[name] || helperText || " "}
        InputLabelProps={{ shrink: true }}
        InputProps={tooltip ? { endAdornment: infoIcon(tooltip) } : undefined}
        sx={fieldSx}
      />
    );
  };

  const dateField = (name, label, opts = {}) => {
    const { required = false, maxDate } = opts;
    return (
      <DatePicker
        label={label}
        value={form[name] ? moment(form[name], "YYYY-MM-DD") : null}
        onChange={(v) => {
          const value = v && v.isValid() ? v.format("YYYY-MM-DD") : "";
          handleInputChange({ target: { name, value, type: "text" } });
        }}
        inputFormat="MM/DD/YYYY"
        minDate={moment(DATE_MIN, "YYYY-MM-DD")}
        maxDate={maxDate || moment(DATE_MAX, "YYYY-MM-DD")}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            required={required}
            name={name}
            error={!!errors[name]}
            helperText={errors[name] || " "}
            onKeyUp={skipDateSeparator}
            sx={fieldSx}
          />
        )}
      />
    );
  };

  const dropdown = (name, label, options, opts = {}) => {
    const { tooltip, required = false } = opts;
    return (
      <FormControl
        fullWidth
        required={required}
        sx={{ "& label.Mui-focused": { color: BRAND } }}
        error={!!errors[name]}
      >
        <InputLabel id={`${name}-label`} shrink>{label}</InputLabel>
        <Select
          name={name}
          labelId={`${name}-label`}
          label={label}
          notched
          displayEmpty
          value={form[name]}
          onChange={handleInputChange}
          endAdornment={tooltip ? infoIcon(tooltip) : null}
          sx={{
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
            "& .MuiSelect-icon": { right: tooltip ? 38 : 7 },
          }}
        >
          <MenuItem value="" disabled>Select</MenuItem>
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.name}</MenuItem>
          ))}
        </Select>
        <Box component="span" sx={{ fontSize: "0.75rem", mt: "3px", ml: "14px", minHeight: "1.25em", color: theme.palette.error.main }}>
          {errors[name] || ""}
        </Box>
      </FormControl>
    );
  };

  const checkbox = (name, label, tooltip) => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              name={name}
              checked={form[name]}
              onChange={handleInputChange}
              sx={{ "&.Mui-checked": { color: BRAND }, "& .MuiSvgIcon-root": { color: BRAND } }}
            />
          }
          label={label}
        />
        {tooltip && (
          <Tooltip title={tooltip} placement="right" arrow>
            <InfoOutlined sx={{ fontSize: "1rem", color: theme.palette.text.secondary }} />
          </Tooltip>
        )}
      </Box>
      {errors[name] && (
        <Box component="span" sx={{ display: "block", fontSize: "0.75rem", ml: 4, mb: 1, color: theme.palette.error.main }}>
          {errors[name]}
        </Box>
      )}
    </Box>
  );

  const grid = (children) => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, columnGap: 3, rowGap: 2 }}>{children}</Box>
  );

  const sectionTitle = (text, sx = {}) => (
    <Typography variant="subtitle2" fontWeight={600} color={theme.palette.text.primary} sx={{ mt: 3, mb: 1.5, ...sx }}>
      {text}
    </Typography>
  );

  const tabsWithErrors = new Set(
    Object.keys(errors).filter((f) => errors[f]).map((f) => FIELD_TAB[f]).filter((t) => t !== undefined)
  );

  return (
   <LocalizationProvider dateAdapter={AdapterMoment}>
    <Box sx={{
      width: "100%", maxWidth: "60rem", height: "92vh", background: "#fefefe",
      display: "flex", flexDirection: "column", padding: "1.5rem", gap: 1.5, mx: "auto",
    }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box>
          <Typography fontSize="1.05rem" fontWeight={600} color={theme.palette.text.primary}>
            USPTO filing fees
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75 }}>
            <Chip label={SCOPE_LABEL} size="small" sx={{ background: "#eef4fb", color: BRAND, fontWeight: 600 }} />
            <Chip label={SCOPE_BASIS} size="small" variant="outlined" />
            <Tooltip title={SCOPE_TOOLTIP} placement="right" arrow>
              <InfoOutlined sx={{ fontSize: "0.95rem", color: theme.palette.text.secondary }} />
            </Tooltip>
          </Box>
        </Box>
        <Button onClick={requestClose} aria-label="Close" sx={{ minWidth: 0, ":hover": { background: "transparent" } }}>
          <Close />
        </Button>
      </Box>

      {confirmDiscard && (
        <Alert
          severity="warning"
          onClose={() => setConfirmDiscard(false)}
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button size="small" onClick={() => setConfirmDiscard(false)} sx={{ textTransform: "none" }}>
                Keep editing
              </Button>
              <Button size="small" color="error" onClick={closeModal} sx={{ textTransform: "none" }}>
                Discard
              </Button>
            </Box>
          }
        >
          Discard this quote? What you've entered will be lost.
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: "divider", "& .MuiTabs-indicator": { backgroundColor: BRAND } }}
      >
        {TABS.map((label, i) => (
          <Tab
            key={label}
            label={tabsWithErrors.has(i) ? `${label} •` : label}
            disabled={i > maxUnlockedTab}
            sx={{
              textTransform: "none",
              color: tabsWithErrors.has(i) ? theme.palette.error.main : activeTab === i ? BRAND : theme.palette.text.secondary,
              "&.Mui-selected": { color: tabsWithErrors.has(i) ? theme.palette.error.main : BRAND },
            }}
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", paddingTop: 3, paddingRight: 1 }}>
        {activeTab === 0 && (
          <>
            {grid(
              <>
                {dateField("filingDate", "Filing date", { required: true })}
                {dropdown("entityStatus", "Entity status", ENTITY_STATUS, {
                  required: true,
                  tooltip: "As determined by the applicant. This calculator applies the rate; it does not verify eligibility.",
                })}
              </>
            )}

            {sectionTitle("Claim counts")}
            {grid(
              <>
                {numberField("totalClaims", "Total claims (fee-adjusted)", {
                  required: true,
                  tooltip: "USPTO counts a multiple dependent claim as the number of claims it refers to (37 CFR 1.75(c)).",
                  helperText: "Fee-adjusted count, not the raw count.",
                })}
                {numberField("independentClaims", "Independent claims (fee-adjusted)", {
                  required: true,
                  tooltip: "USPTO counts a multiple dependent claim as the number of claims it refers to (37 CFR 1.75(c)).",
                  helperText: "Fee-adjusted count, not the raw count.",
                })}
              </>
            )}

            {sectionTitle("Page counts")}
            <Typography fontSize="0.8rem" color={theme.palette.text.secondary} sx={{ mb: 1.5 }}>
              Exclude sequence listing and computer program listing pages.
            </Typography>
            {grid(
              <>
                {numberField("specificationPages", "Specification pages", { required: true })}
                {numberField("claimsPages", "Claims pages", { required: true })}
                {numberField("abstractPages", "Abstract pages", { required: true })}
                {numberField("drawingPages", "Drawing pages")}
              </>
            )}
          </>
        )}

        {activeTab === 1 && (
          <>
            {sectionTitle("Language")}
            <Box sx={{ maxWidth: { xs: "100%", sm: "50%" }, mt: 1 }}>
              {dropdown("applicationLanguage", "Application language", APPLICATION_LANGUAGE)}
            </Box>

            {sectionTitle("DOCX compliance")}
            <Typography fontSize="0.8rem" color={theme.palette.text.secondary} sx={{ mb: 0.5 }}>
              Any one non-compliant section adds a single surcharge.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {checkbox("descriptionDocxCompliant", "Description is DOCX compliant")}
              {checkbox("claimsDocxCompliant", "Claims are DOCX compliant")}
              {checkbox("abstractDocxCompliant", "Abstract is DOCX compliant")}
            </Box>

            {sectionTitle("Claim types")}
            {checkbox("multipleDependentClaimPresent", "Application contains a proper multiple dependent claim")}
          </>
        )}

        {activeTab === 2 && (
          <>
            {sectionTitle("Benefit claim")}
            {checkbox("claimsEarlierApplication", "Claims benefit of an earlier application")}
            {form.claimsEarlierApplication && (
              <Box sx={{ maxWidth: { xs: "100%", sm: "50%" }, mt: 1 }}>
                {dateField("earliestBenefitDate", "Earliest benefit date", {
                  required: true,
                  maxDate: form.filingDate ? moment(form.filingDate, "YYYY-MM-DD") : undefined,
                })}
              </Box>
            )}

            {sectionTitle("Sequence listing")}
            {checkbox("sequenceListingSubmitted", "A sequence listing is being submitted")}
            {form.sequenceListingSubmitted && (
              <Box sx={{ maxWidth: { xs: "100%", sm: "50%" }, mt: 1 }}>
                {numberField("sequenceListingSizeMB", "Sequence listing size (MB)", { required: true })}
              </Box>
            )}

            {sectionTitle("Optional services")}
            {checkbox(
              "trackOneRequested",
              "Request prioritized examination (Track One)",
              "Adds the prioritized examination fee and the processing fee required with it. Requires up to 4 independent and 30 total claims, and no multiple dependent claims."
            )}

            {sectionTitle("Was anything filed late or without a required item?")}
            <Typography fontSize="0.8rem" color={theme.palette.text.secondary} sx={{ mb: 0.5 }}>
              Any one of these adds a single surcharge - checking more than one does not multiply it.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" } }}>
              {checkbox("filingFeePaidLate", "Filing fee paid late")}
              {checkbox("searchFeePaidLate", "Search fee paid late")}
              {checkbox("examFeePaidLate", "Examination fee paid late")}
              {checkbox("oathFiledLate", "Oath or declaration filed late")}
              {checkbox("filedWithoutClaims", "Filed without at least one claim")}
              {checkbox("filedByReference", "Filed by reference")}
            </Box>
          </>
        )}

        {activeTab === 3 && (
          <>
            <Typography fontSize="0.8rem" color={theme.palette.text.secondary} sx={{ mb: 2 }}>
              Recorded on the quote for reference. None of these affect the fee.
            </Typography>
            {grid(
              <>
                {textField("applicationTitle", "Application title")}
                {numberField("numberOfInventors", "Number of inventors")}
              </>
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, borderTop: 1, borderColor: "divider", paddingTop: 2 }}>
        <Button onClick={requestClose} variant="outlined" color="error" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          {activeTab > 0 && (
            <Button
              onClick={() => setActiveTab((prev) => Math.max(0, prev - 1))}
              variant="outlined"
              sx={{ textTransform: "none", color: BRAND, borderColor: BRAND, "&:hover": { borderColor: BRAND } }}
            >
              Back
            </Button>
          )}
          {activeTab < TABS.length - 1 ? (
            <Button
              onClick={handleNext}
              variant="contained"
              sx={{ background: BRAND, borderColor: BRAND, textTransform: "none", "&:hover": { background: BRAND } }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleCalculate}
              variant="contained"
              sx={{ background: BRAND, borderColor: BRAND, textTransform: "none", "&:hover": { background: BRAND } }}
            >
              Get fee quote
            </Button>
          )}
        </Box>
      </Box>
    </Box>
   </LocalizationProvider>
  );
};

export default UsptoFeeCalculatorModal;
